/**
 * ChannelLeaderboard — table primitive that sorts signup channels by
 * volume desc and surfaces each row's conversion rate alongside a
 * volume bar. Anomaly rows (broken funnels — high volume, near-zero
 * attendance) are highlighted with a warning-tinted background and an
 * inline `<AnomalyChip/>` so the user can't miss them.
 *
 * The table layout intentionally mirrors the `.table .num-cell /
 * .cellbar` styles already used by the funnel and organizers views so
 * the visual rhythm carries across modules.
 */
import { AnomalyChip } from '../honesty/AnomalyChip';
import type { AnomalySeverity } from '../honesty/AnomalyChip';
import { formatNumber } from '../../utils/format';

export interface ChannelLeaderboardRow {
  id: string;
  label: string;
  total_signups: number;
  attended: number;
  rate: number; // 0..1
  anomaly?: { severity: AnomalySeverity; reason: string };
}

export interface ChannelLeaderboardProps {
  rows: ChannelLeaderboardRow[];
  /** Override the implicit max for the volume bar; defaults to max(rows.total_signups). */
  maxSignups?: number;
}

export function ChannelLeaderboard({ rows, maxSignups }: ChannelLeaderboardProps) {
  const sorted = [...rows].sort((a, b) => b.total_signups - a.total_signups);
  const implicitMax = Math.max(1, ...sorted.map((r) => r.total_signups));
  const max = Math.max(1, maxSignups ?? implicitMax);

  const numHeader: React.CSSProperties = { textAlign: 'right' };
  const anomalyRowStyle: React.CSSProperties = {
    background: 'var(--warning-soft)',
    outline: '1px solid var(--warning)',
    outlineOffset: -1,
  };

  return (
    <table className="table">
      <thead>
        <tr>
          <th>ערוץ</th>
          <th style={numHeader}>נפח הרשמות</th>
          <th style={numHeader}>הרשמות</th>
          <th style={numHeader}>הגיעו</th>
          <th style={numHeader}>אחוז הגעה</th>
          <th>סטטוס</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const share = r.total_signups / max;
          const isAnomaly = Boolean(r.anomaly);
          const fillClass = isAnomaly
            ? 'fill neg'
            : r.rate >= 0.5
              ? 'fill accent'
              : 'fill';
          return (
            <tr key={r.id} style={isAnomaly ? anomalyRowStyle : undefined}>
              <td>
                <b>{r.label}</b>
              </td>
              <td className="num-cell">
                <div className="cellbar">
                  <div className="track">
                    <div className={fillClass} style={{ width: `${share * 100}%` }} />
                  </div>
                  <div className="v">{formatNumber(r.total_signups)}</div>
                </div>
              </td>
              <td className="num-cell">{formatNumber(r.total_signups)}</td>
              <td className="num-cell">{formatNumber(r.attended)}</td>
              <td className="num-cell">
                {r.total_signups > 0 ? `${Math.round(r.rate * 100)}%` : '—'}
              </td>
              <td>
                {r.anomaly ? (
                  <AnomalyChip severity={r.anomaly.severity} reason={r.anomaly.reason} />
                ) : r.rate >= 0.45 ? (
                  <span className="badge badge-positive">איכות גבוהה</span>
                ) : r.rate > 0.25 ? (
                  <span className="badge">בסדר</span>
                ) : (
                  <span className="badge badge-info">נמוך</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
